import { defineStore } from "pinia";
import api from "@/api";
import { useOrganizationStore } from "@/stores/organization";
import {
  DEFAULT_MAX_RECONNECT_ATTEMPT,
  DEFAULT_RECONNECT_INTERVAL,
  useWfbChat,
} from "@/composables/useWfbChat";

const WFB_CHAT_WIDGET_SESSION_KEY_PREFIX = "ezee.wfb.chat_widget.session_id";
const wfbChat = useWfbChat();

function createMessage(id, msgType, response) {
  return {
    id,
    msg_type: msgType,
    response,
    success: true,
    attachments: [],
    media_urls: [],
    citations: [],
  };
}

/** Return the stable browser session ID for a widget use case. */
function getSessionId(sessionKey) {
  const storageKey = `${WFB_CHAT_WIDGET_SESSION_KEY_PREFIX}.${sessionKey}`;
  let sessionId = window.sessionStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = window.crypto.randomUUID();
    window.sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

export const useWfbChatWidgetStore = defineStore("wfb-chat-widget", {
  state: () => ({
    authToken: null,
    accessToken: null,
    websocket: null,
    connectionStatus: "MANUAL_DISCONNECT",
    history: new Map(),
    sessionKey: null,
    chatOrigin: null,
    title: null,
    subtitle: null,
    extras: {},
    historyThreadId: null,
    isHistoryLoading: false,
    hasLoadedHistory: false,
    loadHistory: true,
    ignoreNextMessage: false,
    isWidgetOpen: false,
    curReconnectAttempts: 0,
    isStreaming: false,
    isStoppingResponse: false,
    streamingRequestId: null,
    recentMessagePayloads: [],
    isInitializing: false,
    reconnectInterval: DEFAULT_RECONNECT_INTERVAL,
    maxReconnectAttempts: DEFAULT_MAX_RECONNECT_ATTEMPT,
  }),
  getters: {
    sessionId(state) {
      return getSessionId(state.sessionKey);
    },
    lastMessage(state) {
      return Array.from(state.history.values()).at(-1);
    },
    historyId(state) {
      return state.historyThreadId || state.extras.dialect_id || state.extras.artifact_id;
    },
    messageExtras(state) {
      return {
        ...state.extras,
        user_token: state.authToken,
      };
    },
  },
  actions: {
    /** Open artifact-builder for the selected workflow or app. */
    async openArtifactEditor(artifact, organizationId) {
      this.closeWidget();
      const artifactType = artifact.type === "app" ? "app" : "workflow";
      await this.open({
        sessionKey: `artifact_builder.edit.${artifact.id}`,
        chatOrigin: "artifact_builder",
        title: `Edit ${artifactType === "app" ? "Web App" : "Workflow"}`,
        subtitle: artifact.display_name || artifact.name,
        extras: {
          mode: "artifact_builder",
          source: "pv2_artifacts_dashboard",
          org_id: organizationId,
          artifact_id: artifact.id,
          artifact_type: artifactType,
        },
        loadHistory: false,
        initialMessage:
          `Use this chat to update the selected ${artifactType}. ` +
          `Changes here only apply to this ${artifactType}.`,
      });
      if (this.isWidgetOpen) await this.viewHistory();
    },

    /** Open a WFB chat widget for the requested use case and session. */
    async open({
      sessionKey,
      chatOrigin,
      title,
      subtitle,
      extras = {},
      historyThreadId = null,
      loadHistory = true,
      initialMessage = null,
    }) {
      if (!sessionKey) return;

      const isNewSession = this.sessionKey !== sessionKey;
      if (isNewSession) {
        this._closeWebsocket();
        this.history = new Map();
        this.recentMessagePayloads = [];
        this.isStreaming = false;
        this.isStoppingResponse = false;
        this.connectionStatus = "MANUAL_DISCONNECT";
      }

      this.sessionKey = sessionKey;
      this.chatOrigin = chatOrigin;
      this.title = title;
      this.subtitle = subtitle;
      this.extras = extras;
      this.historyThreadId = historyThreadId;
      this.isHistoryLoading = false;
      this.hasLoadedHistory = false;
      this.loadHistory = loadHistory;
      this.isWidgetOpen = true;

      if (initialMessage && !this.history.size) {
        const id = `initial.${sessionKey}`;
        this.history.set(id, createMessage(id, "bot", initialMessage));
      }

      await this.initialize();
    },

    /** Load history for the active widget context. */
    async viewHistory() {
      const historyId = this.historyId;
      const sessionKey = this.sessionKey;
      if (
        !historyId ||
        this.isHistoryLoading ||
        this.hasLoadedHistory
      ) return;

      this.isHistoryLoading = true;
      try {
        let response;
        if (this.extras.artifact_id) {
          response = await api.code_orchestrator.fetchArtifactHistory(
            this.extras.org_id,
            historyId,
            this.extras.artifact_type,
          );
        } else {
          const fetchHistory = this.historyThreadId
            ? api.dialects.fetchDialectHistoryByThread
            : api.dialects.fetchDialectHistory;
          response = await fetchHistory(historyId, this.extras.org_id);
        }
        if (
          !this.isWidgetOpen ||
          sessionKey !== this.sessionKey ||
          historyId !== this.historyId
        ) return;

        const historicalMessages = new Map();

        response.data.forEach((interaction) => {
          const userMessageId = `${interaction.interaction_id}.user`;
          historicalMessages.set(userMessageId, {
            ...createMessage(userMessageId, "user", interaction.query),
            user_id: interaction.user_id,
            user_full_name: interaction.user_full_name,
          });

          if (interaction.response) {
            const botMessageId = `${interaction.interaction_id}.bot`;
            historicalMessages.set(
              botMessageId,
              createMessage(botMessageId, "bot", interaction.response),
            );
          }
        });

        this.history = new Map([...historicalMessages, ...this.history]);
        this.hasLoadedHistory = true;
      } catch (error) {
        console.error("Error loading WFB chat history:", error);
      } finally {
        if (sessionKey === this.sessionKey && historyId === this.historyId) {
          this.isHistoryLoading = false;
        }
      }
    },

    /** Close the widget and clear local state */
    closeWidget() {
      this.isWidgetOpen = false;
      this.connectionStatus = "MANUAL_DISCONNECT";
      this.history = new Map();
      this.historyThreadId = null;
      this.isHistoryLoading = false;
      this.hasLoadedHistory = false;
      this.recentMessagePayloads = [];
      this.isStreaming = false;
      this.isStoppingResponse = false;
      this._closeWebsocket();
    },

    /** Initialize the widget and begin connecting to AC. */
    async initialize() {
      if (this.isInitializing) return;
      if (["CONNECTING", "CONNECTED"].includes(this.connectionStatus)) return true;

      this.isInitializing = true;

      try {
        const orgStore = useOrganizationStore();

        if (!orgStore.myOrgEnablements.includes("agentic.adoption")) {
          this.closeWidget();
          return false;
        }

        this.connectionStatus = "DISCONNECTED";
        await this._reconnect();
        return true;
      } finally {
        this.isInitializing = false;
      }
    },

    _closeWebsocket() {
      wfbChat.closeWebsocket(this);
    },

    cleanup() {
      this._closeWebsocket();
      this.$reset();
    },

    async sendMessage(message, requestId, attachments = []) {
      if (!this.websocket) return;

      return wfbChat.sendMessage(this, message, requestId, attachments);
    },

    async stopResponse(requestId) {
      return wfbChat.stopResponse(this, requestId);
    },

    async _fetchAuthTokens() {
      return wfbChat.fetchAuthTokens(this);
    },

    async _failWithMsg(msg) {
      wfbChat.failWithMsg(this, msg);
    },

    /** Connect to WFB while the widget remains disconnected. */
    async _reconnect() {
      while (this.connectionStatus === "DISCONNECTED") {
        this.connectionStatus = "CONNECTING";
        this._closeWebsocket();

        try {
          await this._fetchAuthTokens();
        } catch (err) {
          console.error("error fetching WFB chat widget auth tokens:", err);
          this.connectionStatus = "FAILED";
          return;
        }

        if (this.connectionStatus !== "CONNECTING") return;
        if (!this.accessToken) {
          this._failWithMsg("Failed to authenticate, please refresh your page.");
          return;
        }

        const ws = api.wfb.getWfbWebSocketService(
          this.accessToken,
          this.sessionId,
          this.chatOrigin,
        );
        this.ignoreNextMessage = !this.loadHistory;
        this._bindWebsocket(ws);
        ws.connect();
        this._closeWebsocket();
        this.websocket = ws;
      }
    },

    _bindWebsocket(ws) {
      ws.on("connected", this._wsOnConnected);
      ws.on("disconnected", this._wsOnDisconnected);
      ws.on("error", this._wsOnError);
      ws.addListener(this._wsOnMessage);
    },

    _wsOnConnected() {
      this.connectionStatus = "CONNECTED";
      this.curReconnectAttempts = 0;
    },

    _wsOnDisconnected() {
      if (["MANUAL_DISCONNECT", "FAILED"].includes(this.connectionStatus)) return;

      if (this.curReconnectAttempts >= this.maxReconnectAttempts) {
        this._failWithMsg("Unable to connect to Workflow Builder Agent Messenger.");
        return;
      }

      this.connectionStatus = "DISCONNECTED";
      this.curReconnectAttempts += 1;
      setTimeout(() => this._reconnect(), this.reconnectInterval);
    },

    _wsOnError(evt) {
      console.error("WFB chat widget websocket error:", evt);
      this.connectionStatus = "DISCONNECTED";
    },

    /** Merge WFB history and streaming chunks into local message state. Same as wfbChat.js */
    _wsOnMessage(data) {
      if (this.ignoreNextMessage) {
        this.ignoreNextMessage = false;
        return;
      }

      wfbChat.mergeMessages(this, data, {
        onSystemError: () => {
          this._failWithMsg("The server encountered an error, please refresh your page.");
        },
      });
    },
  },
});
